/**
 * Dialog for copying shifts from one week to target weeks
 * Supports employee/group filtering and conflict strategy selection
 */

import { useState, useMemo, useEffect } from 'react';
import { useLingui } from '@lingui/react';
import { format, startOfWeek, addWeeks, subDays } from 'date-fns';
import { formatDateRange } from '@/lib/format';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Loader2, Copy } from 'lucide-react';
import { useCopyWeek } from '@/hooks/useShifts';
import type { CopyWeekApiRequest } from '@/hooks/useShifts';
import { EmployeeMultiSelect } from './EmployeeMultiSelect';
import { GroupMultiSelect } from './GroupMultiSelect';
// The generated request types `conflict_strategy` as a string enum; using the
// enum members keeps the wire values ('skip' / 'overwrite') byte-identical.
import { shifts_CopyWeekRequest } from '@/api/models/shifts_CopyWeekRequest';

const ConflictStrategy = shifts_CopyWeekRequest.conflict_strategy;

export interface CopyWeekDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sourceWeekStart: Date;
}

export function CopyWeekDialog({
  open,
  onOpenChange,
  sourceWeekStart,
}: CopyWeekDialogProps) {
  const { i18n } = useLingui();
  const copyWeek = useCopyWeek();

  // Target week date (default: next week after source)
  const defaultTargetDate = useMemo(
    () => addWeeks(sourceWeekStart, 1),
    [sourceWeekStart]
  );
  const [targetDate, setTargetDate] = useState<Date>(defaultTargetDate);
  const [weekCount, setWeekCount] = useState<number>(1);
  const [conflictStrategy, setConflictStrategy] = useState<shifts_CopyWeekRequest.conflict_strategy>(
    ConflictStrategy.SKIP
  );
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<string[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setTargetDate(defaultTargetDate);
      setWeekCount(1);
      setConflictStrategy(ConflictStrategy.SKIP);
      setSelectedEmployeeIds([]);
      setSelectedGroupIds([]);
    }
  }, [open, defaultTargetDate]);

  // Ensure target date is always a Monday
  const targetMonday = useMemo(
    () => startOfWeek(targetDate, { weekStartsOn: 1 }),
    [targetDate]
  );

  // Format source week label
  const sourceWeekLabel = useMemo(() => {
    const end = addWeeks(sourceWeekStart, 1);
    return formatDateRange(sourceWeekStart, subDays(end, 1), i18n.locale, { style: 'medium' });
  }, [sourceWeekStart, i18n.locale]);

  const handleTargetDateChange = (date: Date | undefined) => {
    if (date) {
      setTargetDate(date);
    }
  };

  const handleSubmit = () => {
    const request: CopyWeekApiRequest = {
      source_start_date: format(sourceWeekStart, 'yyyy-MM-dd'),
      target_start_date: format(targetMonday, 'yyyy-MM-dd'),
      target_week_count: weekCount,
      conflict_strategy: conflictStrategy,
      ...(selectedEmployeeIds.length > 0 && { employee_ids: selectedEmployeeIds }),
      ...(selectedGroupIds.length > 0 && { group_ids: selectedGroupIds }),
    };

    copyWeek.mutate(request, {
      onSuccess: () => {
        setTargetDate(defaultTargetDate);
        setWeekCount(1);
        setConflictStrategy(ConflictStrategy.SKIP);
        setSelectedEmployeeIds([]);
        setSelectedGroupIds([]);
        onOpenChange(false);
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Copy className="h-5 w-5" />
            {i18n._({ id: 'shifts.copyWeek.title', message: 'Copy Week' })}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {i18n._({
              id: 'shifts.copyWeek.description',
              message: 'Copy shifts from one week to one or more target weeks',
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Source Week (read-only) */}
          <div className="space-y-2">
            <Label>
              {i18n._({ id: 'shifts.copyWeek.sourceWeek', message: 'Source Week' })}
            </Label>
            <div className="h-10 flex items-center px-3 text-sm border rounded-md bg-muted">
              {sourceWeekLabel}
            </div>
          </div>

          {/* Target Week */}
          <div className="space-y-2">
            <Label>
              {i18n._({ id: 'shifts.copyWeek.targetWeek', message: 'Target Week' })}
            </Label>
            <DatePicker
              value={targetMonday}
              onChange={handleTargetDateChange}
              placeholder={i18n._({
                id: 'shifts.copyWeek.selectTargetWeek',
                message: 'Select target week',
              })}
              showClearButton={false}
            />
            <p className="text-xs text-muted-foreground">
              {i18n._({
                id: 'shifts.copyWeek.targetWeekHint',
                message: 'Shifts will be copied starting from the Monday of the selected week',
              })}
            </p>
            <p className="text-xs text-muted-foreground" data-testid="copy-week-draft-note">
              {i18n._({
                id: 'shifts.copyWeek.draftNote',
                message:
                  'The copies are drafts; publish the target week when it is ready. Days with an approved absence are skipped.',
              })}
            </p>
          </div>

          {/* Week Count */}
          <div className="space-y-2">
            <Label>
              {i18n._({ id: 'shifts.copyWeek.weekCount', message: 'Number of Weeks' })}
            </Label>
            <Select
              value={String(weekCount)}
              onValueChange={(val) => setWeekCount(Number(val))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: 12 }, (_, i) => i + 1).map((num) => (
                  <SelectItem key={num} value={String(num)}>
                    {num} {num === 1
                      ? i18n._({ id: 'shifts.copyWeek.week', message: 'week' })
                      : i18n._({ id: 'shifts.copyWeek.weeks', message: 'weeks' })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Employee Filter */}
          <div className="space-y-2">
            <Label>
              {i18n._({
                id: 'shifts.copyWeek.filterEmployees',
                message: 'Filter by employees',
              })}
            </Label>
            <EmployeeMultiSelect
              value={selectedEmployeeIds}
              onChange={setSelectedEmployeeIds}
              id="copy-week-employees"
            />
            <p className="text-xs text-muted-foreground">
              {i18n._({
                id: 'shifts.copyWeek.filterEmployeesHint',
                message: 'Leave empty to copy shifts for all employees',
              })}
            </p>
          </div>

          {/* Group Filter */}
          <div className="space-y-2">
            <Label>
              {i18n._({
                id: 'shifts.copyWeek.filterGroups',
                message: 'Filter by groups',
              })}
            </Label>
            <GroupMultiSelect
              value={selectedGroupIds}
              onChange={setSelectedGroupIds}
              id="copy-week-groups"
            />
            <p className="text-xs text-muted-foreground">
              {i18n._({
                id: 'shifts.copyWeek.filterGroupsHint',
                message: 'Leave empty to copy shifts for all groups',
              })}
            </p>
          </div>

          {/* Conflict Strategy */}
          <div className="space-y-2">
            <Label>
              {i18n._({
                id: 'shifts.copyWeek.conflictStrategy',
                message: 'If conflicts exist',
              })}
            </Label>
            <RadioGroup
              value={conflictStrategy}
              onValueChange={(val) =>
                setConflictStrategy(
                  val === ConflictStrategy.OVERWRITE
                    ? ConflictStrategy.OVERWRITE
                    : ConflictStrategy.SKIP
                )
              }
              className="space-y-2"
            >
              <div className="flex items-start space-x-2">
                <RadioGroupItem value={ConflictStrategy.SKIP} id="conflict-skip" className="mt-0.5" />
                <div>
                  <Label htmlFor="conflict-skip" className="font-normal cursor-pointer">
                    {i18n._({ id: 'shifts.copyWeek.skip', message: 'Skip conflicts' })}
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    {i18n._({
                      id: 'shifts.copyWeek.skipHint',
                      message: 'An employee who already has a shift at that time keeps it; the copy is left out.',
                    })}
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-2">
                <RadioGroupItem value={ConflictStrategy.OVERWRITE} id="conflict-overwrite" className="mt-0.5" />
                <div>
                  <Label htmlFor="conflict-overwrite" className="font-normal cursor-pointer">
                    {i18n._({
                      id: 'shifts.copyWeek.overwrite',
                      message: 'Overwrite existing',
                    })}
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    {i18n._({
                      id: 'shifts.copyWeek.overwriteHint',
                      message: 'The existing shift at that time is replaced by the copy.',
                    })}
                  </p>
                </div>
              </div>
            </RadioGroup>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={copyWeek.isPending}
          >
            {i18n._({ id: 'common.cancel', message: 'Cancel' })}
          </Button>
          <Button onClick={handleSubmit} disabled={copyWeek.isPending}>
            {copyWeek.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Copy className="mr-2 h-4 w-4" />
            )}
            {i18n._({ id: 'shifts.copyWeek.button', message: 'Copy Week' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
